import {
  mockApiBrands,
  mockCountries,
  mockApiProducts,
  mockProductFilterOptions,
} from "./data.js";
import { getProductConfigurations, validateLaunchDates } from "./matrix.js";
import { validateProductPrice } from "./price.js";

// Explicit demo backend, shared by Vite middleware and the file:// transport.
// Each instance owns its prices. Nothing persists across a server/page restart.
const clone = (value) => JSON.parse(JSON.stringify(value));
const productDto = ({ country, ...product }) => clone(product);
const badRequest = (message) =>
  Object.assign(new Error(message), { status: 400 });

function parseQuery(params) {
  const numeric = (key, fallback) => {
    const value = params.get(key);
    if (value === null || value === "") return fallback;
    const number = Number(value);
    if (!Number.isFinite(number) || number < 0)
      throw badRequest(`Invalid ${key}`);
    return number;
  };
  const query = {
    query: (params.get("query") || "").trim().toLocaleLowerCase(),
    // Repeated brandIds parameters; no selection deliberately matches no products.
    brandIds: params.getAll("brandIds").filter(Boolean),
    countryId: params.get("countryId") || "",
    ram: numeric("ram", 0),
    storage: numeric("storage", 0),
    network: params.get("network") || "",
    launchStart: params.get("launchStart") || "",
    launchEnd: params.get("launchEnd") || "",
    minPrice: numeric("minPrice", 0),
    maxPrice: numeric("maxPrice", Number.MAX_SAFE_INTEGER),
  };
  const dateError = validateLaunchDates(query.launchStart, query.launchEnd);
  if (dateError) throw badRequest(dateError);
  if (query.minPrice > query.maxPrice)
    throw badRequest("minPrice must not exceed maxPrice");
  return query;
}

export function createMockBackend({
  brands = mockApiBrands,
  countries = mockCountries,
  products = mockApiProducts,
  filterOptions = mockProductFilterOptions,
} = {}) {
  const brandDictionary = clone(brands);
  const countryDictionary = clone(countries);
  const optionDictionary = clone(filterOptions);
  const records = products.map(productDto);
  const brandNames = new Map(
    brandDictionary.map((brand) => [String(brand.id), brand.name]),
  );

  return {
    handle(method, input, body) {
      try {
        const url = new URL(input, "http://mock.local");
        const path = url.pathname;
        if (method === "GET" && path === "/api/brands")
          return { status: 200, body: clone(brandDictionary) };
        if (method === "GET" && path === "/api/countries")
          return { status: 200, body: clone(countryDictionary) };
        if (method === "GET" && path === "/api/product-filter-options")
          return { status: 200, body: clone(optionDictionary) };
        if (method === "GET" && path === "/api/products") {
          const q = parseQuery(url.searchParams);
          const items = records
            .filter((p) => {
              const configurations = getProductConfigurations(p);
              const searchText =
                `${p.name} ${p.brandId} ${brandNames.get(String(p.brandId)) || ""} ${configurations.map(([ram, storage]) => `${ram}GB+${storage}GB`).join(" ")}`.toLocaleLowerCase();
              return (
                q.brandIds.includes(String(p.brandId)) &&
                p.price >= q.minPrice &&
                p.price <= q.maxPrice &&
                (!q.query || searchText.includes(q.query)) &&
                (!q.countryId || String(p.countryId) === q.countryId) &&
                (!q.network || p.network === q.network) &&
                (!(q.ram || q.storage) ||
                  configurations.some(
                    ([ram, storage]) =>
                      (!q.ram || ram === q.ram) &&
                      (!q.storage || storage === q.storage),
                  )) &&
                (!q.launchStart || p.launchDate >= q.launchStart) &&
                (!q.launchEnd || p.launchDate <= q.launchEnd)
              );
            })
            .sort((a, b) => b.price - a.price || a.id.localeCompare(b.id));
          return {
            status: 200,
            body: { items: clone(items), total: items.length },
          };
        }
        const priceMatch = path.match(/^\/api\/products\/([^/]+)\/price$/);
        if (method === "PATCH" && priceMatch) {
          if (
            !body ||
            typeof body !== "object" ||
            Array.isArray(body) ||
            !["string", "number"].includes(typeof body.price)
          )
            throw badRequest("A price is required");
          const error = validateProductPrice(body.price);
          if (error) throw badRequest(error);
          const record = records.find(
            (p) => String(p.id) === decodeURIComponent(priceMatch[1]),
          );
          if (!record)
            return { status: 404, body: { message: "Product not found" } };
          record.price = Number(body.price);
          return { status: 200, body: clone(record) };
        }
        if (
          [
            "/api/brands",
            "/api/countries",
            "/api/product-filter-options",
            "/api/products",
          ].includes(path) ||
          priceMatch
        )
          return { status: 405, body: { message: "Method not allowed" } };
        return {
          status: 404,
          body: { message: "Mock API endpoint not found" },
        };
      } catch (error) {
        return {
          status: error.status || 400,
          body: { message: error.message || "Invalid request" },
        };
      }
    },
  };
}

export function createMockTransport(backend = createMockBackend()) {
  return async (input, { method = "GET", body, signal } = {}) => {
    signal?.throwIfAborted();
    // Give pending aborts a chance to cancel before performing an in-memory write.
    await Promise.resolve();
    signal?.throwIfAborted();
    let payload;
    try {
      payload = body === undefined ? undefined : JSON.parse(body);
    } catch {
      return new Response(JSON.stringify({ message: "Invalid JSON" }), {
        status: 400,
        headers: { "Content-Type": "application/json" },
      });
    }
    const result = backend.handle(method.toUpperCase(), input, payload);
    signal?.throwIfAborted();
    return new Response(JSON.stringify(result.body), {
      status: result.status,
      headers: { "Content-Type": "application/json" },
    });
  };
}

// Only api.js's explicit file:// branch uses this transport. HTTP never falls back.
export const offlineMockTransport = createMockTransport();
